import { ClaimMessage } from '@/api/claims'
import { FilePicker, Input, TextArea, Typography } from '@/components'
import { ThemeColor } from '@/enums'
import { Button } from '@surein/ui'
import { Paperclip, Send, X } from 'lucide-react'
import { DragEvent, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AttachmentChip,
  AttachmentsRow,
  ComposeActions,
  ComposeContainer,
  RemoveAttachmentButton,
  ReplyBanner,
} from './styles'

export type Attachment = { file_id: string; name: string }

type ComposeFormProps = {
  replyTo: ClaimMessage | null
  subject: string
  body: string
  attachments: Attachment[]
  maxAttachments: number
  isUploading: boolean
  isSending: boolean
  onSubjectChange: (subject: string) => void
  onBodyChange: (body: string) => void
  onFilesPicked: (files: File[]) => void
  onRemoveAttachment: (fileId: string) => void
  onCancelReply: () => void
  onSend: () => void
}

const ComposeForm = ({
  replyTo,
  subject,
  body,
  attachments,
  maxAttachments,
  isUploading,
  isSending,
  onSubjectChange,
  onBodyChange,
  onFilesPicked,
  onRemoveAttachment,
  onCancelReply,
  onSend,
}: ComposeFormProps) => {
  const { t } = useTranslation()
  const [isDragOver, setIsDragOver] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const replyToId = replyTo?.item_id

  // Bring an inline reply form into view next to the message it answers
  useEffect(() => {
    if (!replyToId) return
    const container = containerRef.current
    container?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    container?.querySelector('textarea')?.focus({ preventScroll: true })
  }, [replyToId])

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragOver(false)
    const files = Array.from(e.dataTransfer.files)
    if (files.length > 0) {
      onFilesPicked(files)
    }
  }

  return (
    <ComposeContainer
      ref={containerRef}
      $isDragOver={isDragOver}
      onDragOver={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault()
        setIsDragOver(true)
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      {replyTo && (
        <ReplyBanner>
          <Typography color={ThemeColor.b50} variant="p2Body" ellipsis>
            {t('claimsPage.detail.messages.replyingTo', {
              subject:
                replyTo.subject || t('claimsPage.detail.messages.noSubject'),
            })}
          </Typography>
          <RemoveAttachmentButton
            onClick={onCancelReply}
            aria-label={t('cancel')}
          >
            <X size={14} />
          </RemoveAttachmentButton>
        </ReplyBanner>
      )}
      <Input
        placeholder={t('claimsPage.detail.messages.subjectPlaceholder')}
        value={subject}
        onChange={(e) => onSubjectChange(e.target.value)}
      />
      <TextArea
        rows={3}
        placeholder={t('claimsPage.detail.messages.bodyPlaceholder')}
        value={body}
        onChange={(e) => onBodyChange(e.target.value)}
      />
      {attachments.length > 0 && (
        <AttachmentsRow>
          {attachments.map((attachment) => (
            <AttachmentChip key={attachment.file_id}>
              <Typography variant="captionR" ellipsis>
                {attachment.name}
              </Typography>
              <RemoveAttachmentButton
                onClick={() => onRemoveAttachment(attachment.file_id)}
                aria-label={t('claimsPage.detail.messages.removeAttachment')}
              >
                <X size={12} />
              </RemoveAttachmentButton>
            </AttachmentChip>
          ))}
        </AttachmentsRow>
      )}
      <ComposeActions>
        <FilePicker
          multiple
          perFile
          max={maxAttachments - attachments.length}
          onPick={onFilesPicked}
        >
          <Button icon={Paperclip} variant="outline" disabled={isUploading}>
            {t('claimsPage.detail.messages.attachFiles')}
          </Button>
        </FilePicker>
        <Button
          icon={Send}
          disabled={!body.trim() || isSending || isUploading}
          onClick={onSend}
        >
          {t('claimsPage.detail.messages.send')}
        </Button>
      </ComposeActions>
    </ComposeContainer>
  )
}

export default ComposeForm
